/** The three possible readiness outcomes, in order of caution. */
export type OutcomeKey = 'soon' | 'foster' | 'ready';

export const OUTCOME_KEYS: readonly OutcomeKey[] = ['soon', 'foster', 'ready'];

/** Per-option contribution toward each outcome. */
export interface OutcomeWeights {
  soon: number;
  foster: number;
  ready: number;
}

export interface ReadinessOption extends OutcomeWeights {
  value: string;
  label: string;
}

export interface ReadinessQuestion {
  id: string;
  prompt: string;
  options: ReadinessOption[];
}

export interface OutcomeCopy {
  badge: string;
  heading: string;
  body: string;
}

export interface ReadinessLink {
  label: string;
  url: string;
}

export interface ReadinessCallout {
  title: string;
  text: string;
  logoUrl: string | null;
  links: ReadinessLink[];
}

export interface ReadinessNextItem {
  title: string;
  category: string;
  url: string;
  imageUrl: string | null;
}

export interface OutcomeContent extends OutcomeCopy {
  callout: ReadinessCallout | null;
  whatsNext: ReadinessNextItem[];
}

export type OutcomeOverride = Partial<OutcomeCopy> & {
  callout?: ReadinessCallout | null;
  whatsNext?: ReadinessNextItem[];
};

export type OutcomeCopyOverrides = Partial<Record<OutcomeKey, OutcomeOverride>>;

export type ReadinessStepLabels = [string, string, string, string, string];

export interface ReadinessPageCopy {
  pageTitle: string;
  introHeading: string;
  introText: string;
  introImageUrl: string;
  stepsVisible: boolean;
  stepsHeading: string;
  stepLabels: ReadinessStepLabels;
  whatsNextTitle: string;
  disclaimer: string;
}

export type ReadinessPageOverrides = Partial<
  Pick<
    ReadinessPageCopy,
    | 'pageTitle'
    | 'stepsVisible'
    | 'stepsHeading'
    | 'stepLabels'
    | 'whatsNextTitle'
    | 'disclaimer'
  >
>;

export type PetLossLocale = 'en' | 'fr';

export const PET_LOSS_LOCALES: readonly PetLossLocale[] = ['en', 'fr'];

export interface AnswerSnapshot {
  questionId: string;
  prompt: string;
  optionValue: string;
  optionLabel: string;
}

export interface PetLossPartnerPublicProfile {
  slug: string;
  practiceName: string;
  practitionerName: string | null;
  bio: string | null;
  logoUrl: string | null;
  headshotUrl: string | null;
  introHeading: string | null;
  introText: string | null;
  introImageUrl: string | null;
  pageCopy: ReadinessPageOverrides;
  locale: PetLossLocale;
  questions: ReadinessQuestion[];
  outcomes: OutcomeCopyOverrides;
}
